import {
  formatQueryRes,
  regionHaInstanceApi,
  useAppDispatch,
  useAppSelector,
  useGetHaInstanceExtraInfoQuery,
  useResizeMutation
} from '@monorepo/shared';
import { selectCurrentAccount } from '@monorepo/shared/store/accountSlice';
import { selectAccountAllInstances } from '@monorepo/shared/store/haInstanceSlice';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonToolbar,
  DatePicker,
  FlexboxGrid,
  Loader,
  Message,
  Radio,
  RadioGroup,
  Stack,
  Table
} from 'rsuite';
import {
  getDateStr,
  getDefaultDate,
  getEtaMins,
  getSuggestedTimeStr,
  isDisabledDate,
  isDisabledHour,
  isDisabledMinute
} from '../../utils/schedule';
import { ActionConfirm } from '../../components/ActionConfirm';
import { InstanceStatus } from './InstanceStatus';
import { InstanceAction } from '../../config/const';
import { useTimer } from 'react-timer-hook';

export const Resize = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instanceId, accountId } = useParams();
  const [instanceResize, resizeResult] = useResizeMutation();
  const accountRegionInstances = useAppSelector(selectAccountAllInstances);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [instance, setInstance] = useState<Record<string, any>>({});
  const [region, setRegion] = useState<Record<string, any>>({});
  const [isCheck, setIsCheck] = useState<boolean>(false);
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [value, setValue] = useState<string>('suggested');
  const [selectedDate, setSelectedDate] = useState<Date>(getDefaultDate());
  const [tableData, setTableData] = useState<Record<string, any>[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<Record<string, any>>({
    type: '',
    content: '',
    header: ''
  });
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [confirmOpts, setConfirmOpts] = useState<Record<string, any>[]>([]);
  const { restart } = useTimer({
    expiryTimestamp: new Date('2999-12-31'),
    autoStart: false,
    onExpire: () => {
      setIsCheck(true);
    }
  });
  const handleChange = (d: Date) => {
    setSelectedDate(d);
  };
  const submit = () => {
    const etaMins = getEtaMins(selectedDate);
    if (etaMins < 0) {
      setMessage({
        type: 'error',
        header: 'A problem occurred:',
        content: 'Select time not correct!'
      });
      setShowMessage(true);
    } else {
      setShowMessage(false);
      setConfirmOpts([
        {
          name: 'Account ID',
          value: accountId
        },
        {
          name: 'Instance ID',
          value: instanceId
        },
        {
          name: 'Scheduled Time',
          value: value === 'now' ? 'Right Now' : getDateStr(selectedDate)
        },
        {
          name: 'Resource Upgrade Detail',
          format: () => {
            return (
              <DetailTable
                data={tableData}
                loading={instanceExtraInfoLoading || instanceExtraInfoFetching}
              />
            );
          }
        }
      ]);
      setOpenConfirm(true);
    }
  };
  const onConfirm = () => {
    const etaMins = getEtaMins(selectedDate);
    // if (value === 'now') {
    //   setIsCheck(true);
    // } else {
    //   setIsScheduled(true);
    // }
    instanceResize({
      account_id: accountId,
      user_id: currentAccount.user_id,
      access_from_user_id:
        currentAccount.access_from_user_id ||
        currentAccount.access_from?.user_id,
      instance_id: instanceId,
      region: region,
      eta_mins: etaMins
    });
  };
  const {
    data: instanceExtraInfo,
    isLoading: instanceExtraInfoLoading,
    isFetching: instanceExtraInfoFetching
  } = useGetHaInstanceExtraInfoQuery(
    formatQueryRes({
      account: currentAccount,
      instance_id: instanceId,
      region
    }),
    { skip: !region.token }
  );

  useEffect(() => {
    // const demoData = {
    //   disk_size: 50,
    //   total_disk: 200,
    //   vcpus: 4,
    //   new_vcpus: 12
    // };
    // const resize = instanceExtraInfo?.resize || demoData;
    if (instanceExtraInfo) {
      const resize = instanceExtraInfo?.resize || {};
      if (resize) {
        const scheduledTime = resize?.scheduled_time;
        const createTime = resize?.ctime;
        const tData: Record<string, any>[] = [];
        if (scheduledTime) {
          const scheduledDate: Date = new Date(scheduledTime);
          const diffMs = scheduledDate.getTime() - new Date().getTime(); // milliseconds
          const diffMins = Math.abs(Math.round(diffMs / 1000 / 60)); // minutes
          console.info(
            'resize scheduled time from extra info:',
            scheduledDate,
            ',diff in minutes:',
            diffMins
          );
          if (diffMins < 5) {
            setIsCheck(true);
          } else {
            setIsScheduled(true);
            restart(scheduledDate);
          }
        }
        if (resize?.total_disk || resize?.disk_size) {
          tData.push({
            name: 'Disk',
            upTo: resize?.total_disk ? resize?.total_disk + ' G' : '',
            current: resize?.disk_size ? resize?.disk_size + ' G' : ''
          });
          if (resize?.new_vcpus) {
            tData.push({
              name: 'CPU',
              upTo: resize?.new_vcpus,
              current: resize?.vcpus
            });
          }
          if (resize.new_ram) {
            tData.push({
              name: 'RAM',
              upTo: resize?.new_ram,
              current: resize?.ram
            });
          }
          setTableData(tData);
        }
        setConfirmOpts([
          {
            name: 'Account ID',
            value: accountId
          },
          {
            name: 'Instance ID',
            value: instanceId
          },
          {
            name: 'Scheduled Time',
            value: getDateStr(new Date(scheduledTime))
          },
          {
            name: 'Resource Upgrade Detail',
            format: () => {
              return <DetailTable data={tData} />;
            }
          }
        ]);
      }
    }
  }, [instanceExtraInfo]);

  useEffect(() => {
    const accountData = accountRegionInstances?.[accountId || ''];
    if (accountData && instanceId) {
      const inst = accountData.find(
        (obj: Record<string, any>) => obj.instance_id == instanceId
      );
      inst && setInstance({ ...inst });
      inst?.region && setRegion(inst.region);
    }
  }, [accountRegionInstances, instanceId]);

  useEffect(() => {
    if (value === 'now') {
      setSelectedDate(new Date());
    } else if (value === 'suggested') {
      setSelectedDate(getDefaultDate());
    }
  }, [value]);

  useEffect(() => {
    setOpenConfirm(false);
    if (resizeResult.error) {
      const data: Record<string, any> = resizeResult.error;
      const code = data?.error_code || data?.status;
      const messageText =
        data?.error_desc || data?.message || 'Submit Resize Failed!';
      if (messageText) {
        setMessage({
          type: 'error',
          header: 'A problem occurred:',
          content: `${messageText} [Error_code: ${code}]`
        });
        setShowMessage(true);
      }
    } else if (resizeResult.data) {
      if (value === 'now') {
        setIsCheck(true);
      } else {
        setIsScheduled(true);
        restart(selectedDate);
      }
    }
  }, [resizeResult]);
  return (
    <div className=".scroll-full-page">
      {resizeResult.isLoading ? (
        <div style={{ height: '90%', position: 'relative' }}>
          <Loader center size="md" />
        </div>
      ) : (
        <>
          {showMessage && (
            <Message
              showIcon
              type={message?.type}
              header={message?.header}
              style={{ marginBottom: '20px' }}
            >
              <ul>{message?.content && <li>{message?.content}</li>}</ul>
            </Message>
          )}
          {!isScheduled && !isCheck ? (
            <>
              <Stack
                direction="column"
                style={{ marginBottom: '25px' }}
                spacing={20}
                alignItems="stretch"
              >
                <Stack.Item>
                  <b>Resource Upgrade Information</b>
                </Stack.Item>
                <Stack.Item grow={1}>
                  <DetailTable
                    data={tableData}
                    loading={
                      instanceExtraInfoLoading || instanceExtraInfoFetching
                    }
                  />
                </Stack.Item>
              </Stack>
              <RadioGroup
                name="radio-group-controlled"
                value={value}
                onChange={(v: any) => setValue(`${v}`)}
              >
                <Radio value="suggested">
                  Suggested Resize time: Today at {getSuggestedTimeStr()}
                </Radio>
                <Radio value="scheduled">Select a time</Radio>
                <DatePicker
                  value={selectedDate}
                  onChange={(d: any) => handleChange(d)}
                  disabled={value !== 'scheduled'}
                  placeholder={'Schedule upgrade'}
                  format="yyyy-MM-dd HH:mm"
                  size="md"
                  shouldDisableDate={(d) => isDisabledDate(d)}
                  shouldDisableHour={(h, d) => isDisabledHour(h, d)}
                  shouldDisableMinute={(m, d) => isDisabledMinute(m, d)}
                  block
                  // placement="auto"
                  placement="bottomStart"
                />
                <Radio value="now">Resize now</Radio>
              </RadioGroup>
              <ButtonToolbar
                style={{ justifyContent: 'left', marginTop: '10%' }}
              >
                <Button
                  appearance="ghost"
                  className="action-btn"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  // size="sm"
                  onClick={() => {
                    submit();
                  }}
                  className="action-btn"
                  appearance="primary"
                >
                  {'Submit'}
                </Button>
              </ButtonToolbar>
              <ActionConfirm
                open={openConfirm}
                setOpen={setOpenConfirm}
                displayOpts={confirmOpts}
                title={'Resource Upgrade Confirm'}
                onConfirm={onConfirm}
              />
            </>
          ) : (
            <>
              <div style={{ minHeight: '120px' }}>
                <h6 style={{ margin: '20px' }}>
                  {'Resource Upgrade Schedule Information'}
                </h6>
                <ul>
                  {confirmOpts?.map(
                    (obj: Record<string, any>, index: number) => {
                      return (
                        <li key={index} style={{ padding: '6px 0' }}>
                          <FlexboxGrid>
                            {/*icon*/}
                            <FlexboxGrid.Item colspan={6}>
                              {obj?.name}
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item
                              colspan={16}
                              style={{ fontWeight: 'bold' }}
                            >
                              {obj?.format
                                ? obj.format(obj?.value)
                                : obj?.value}
                            </FlexboxGrid.Item>
                          </FlexboxGrid>
                        </li>
                      );
                    }
                  )}
                  <>
                    {isCheck && (
                      <li key={'status'} style={{ padding: '6px 0' }}>
                        <FlexboxGrid>
                          <FlexboxGrid.Item colspan={6}>
                            {'Instance Status'}
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item colspan={16}>
                            <InstanceStatus
                              action={InstanceAction.resize}
                              group_id={currentAccount?.group_id}
                              account_id={accountId || ''}
                              user_id={currentAccount?.user_id || ''}
                              access_from_user_id={
                                currentAccount?.access_from?.user_id || ''
                              }
                              instance_id={instanceId || ''}
                              region={region}
                              license_sn={instance?.license_sn}
                              textStyle={{ color: '#0445d1', fontSize: '14px' }}
                              callBackFn={(ob: any) => {
                                dispatch(
                                  regionHaInstanceApi.util.invalidateTags([
                                    'RegionInstance'
                                  ])
                                );
                                navigate(-1);
                              }}
                            />
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                      </li>
                    )}
                  </>
                </ul>
              </div>
              <ButtonToolbar
                style={{
                  justifyContent: 'left',
                  marginTop: '10%',
                  marginLeft: '28px'
                }}
              >
                <Button
                  appearance="ghost"
                  className="action-btn"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  Close
                </Button>
              </ButtonToolbar>
            </>
          )}
        </>
      )}
    </div>
  );
};
interface IResizeDetailProps {
  data: Record<string, any>[];
  loading?: boolean;
}
const DetailTable = ({ data, loading }: IResizeDetailProps) => {
  const { Column, HeaderCell, Cell } = Table;
  const [isLoading, setIsLoading] = useState<boolean>(!!loading);
  const CompactCell = (props: any) => (
    // <Cell {...props} style={{ padding: 4 }} />
    <Cell {...props} />
  );
  return (
    <Table
      loading={isLoading}
      data={data}
      bordered
      cellBordered
      autoHeight
      affixHeader
      affixHorizontalScrollbar
    >
      <Column flexGrow={1}>
        <HeaderCell>Resource</HeaderCell>
        <CompactCell dataKey="name" />
      </Column>
      <Column flexGrow={1}>
        <HeaderCell>Current</HeaderCell>
        <CompactCell dataKey="current" />
      </Column>
      <Column flexGrow={1}>
        <HeaderCell>Upgrade To</HeaderCell>
        <CompactCell dataKey="upTo" />
      </Column>
    </Table>
  );
};
